import React from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, BookOpen, Radio, Award, FileText } from 'lucide-react';

const works = [
  {
    title: 'I Am Cecilia',
    type: 'YA Novel',
    description: 'Amazon best-selling young adult novel exploring identity, voice, and coming of age.',
    link: 'https://www.amazon.com/I-am-Cecilia-Zara-Miller-ebook/dp/B094519D7T',
    icon: BookOpen,
    image: 'https://customer-assets.emergentagent.com/job_literary-zara/artifacts/wdbxzia0_Concept%20art%202.jpg',
    featured: true,
  },
  {
    title: 'Teen Magazine',
    type: 'Portfolio',
    description: 'Collection of articles, interviews, and cultural commentary for Harvard-led worldwide publication.',
    link: 'https://www.theteenmagazine.com/writers/zara-miller?mode=public',
    icon: FileText,
    image: 'https://customer-assets.emergentagent.com/job_literary-zara/artifacts/jjcyeckg_image.png',
  },
  {
    title: 'Midnight Express',
    type: 'Radio Drama',
    description: 'Co-written and co-directed audio drama showcasing narrative storytelling across media.',
    link: 'https://midnight-express.kit.com/',
    icon: Radio,
    image: 'https://customer-assets.emergentagent.com/job_literary-zara/artifacts/1eh8qfy0_midnight1-copia.png',
  },
  {
    title: 'Influential Women',
    type: 'Feature',
    description: 'Profile feature highlighting work in literary and editorial spaces.',
    link: 'https://influentialwomen.com/connect/zara-miller',
    icon: Award,
    image: 'https://customer-assets.emergentagent.com/job_literary-zara/artifacts/x9fyex3r_1701191099450.jpeg',
  },
  {
    title: 'The Relatable Voices',
    type: 'Anthology Series',
    description: 'Featured in Washington Mail for anthology series exploring diverse narratives.',
    link: 'https://washington-mail.com/the-anthology-relatable-voices-is-out-now/',
    icon: FileText,
    image: 'https://customer-assets.emergentagent.com/job_literary-zara/artifacts/5j2is5qo_image.png',
  },
  {
    title: 'Ellie&Jay Publishing',
    type: 'International Anthology',
    description: 'Contributor to UK-based anthology, expanding literary reach internationally.',
    link: 'https://www.amazon.com/dp/1068332190/ref=mes-dp?',
    icon: BookOpen,
    image: 'https://customer-assets.emergentagent.com/job_literary-zara/artifacts/0z44e3mq_image.png',
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const WorkCard = ({ work, index }) => {
  const Icon = work.icon;
  return (
    <motion.a
      href={work.link}
      target="_blank"
      rel="noopener noreferrer"
      variants={itemVariants}
      className={`group relative overflow-hidden rounded-2xl bg-white hover:shadow-soft-lg transition-all duration-500 border-2 border-pink-blush/50 hover:border-pink-rose ${
        work.featured ? 'md:col-span-2 lg:col-span-1' : ''
      }`}
      data-testid={`work-card-${index}`}
    >
      {work.image && (
        <div className="w-full h-48 overflow-hidden">
          <img
            src={work.image}
            alt={work.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        </div>
      )}

      <div className="p-8">
        <div className="flex items-start justify-between mb-6">
          <div className="p-3 rounded-xl bg-pink-soft border-2 border-pink-blush">
            <Icon className="w-6 h-6 text-pink-mauve" />
          </div>
          <ExternalLink className="w-5 h-5 text-pink-deep group-hover:text-pink-mauve transition-colors" />
        </div>
        <p className="text-xs tracking-widest uppercase text-pink-deep font-sans mb-2">{work.type}</p>
        <h3 className="text-2xl font-serif font-semibold text-brown-deep mb-3 group-hover:text-pink-mauve transition-colors">
          {work.title}
        </h3>
        <p className="text-base leading-relaxed text-brown-warm">{work.description}</p>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-pink-rose to-pink-deep transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
    </motion.a>
  );
};

const FeaturedWork = () => (
  <section id="work" className="py-24 md:py-32 bg-beige-light px-6 md:px-12 lg:px-24 relative overflow-hidden">
    <div className="absolute bottom-10 left-10 w-64 h-40 opacity-10 pointer-events-none">
      <img
        src="https://static.prod-images.emergentagent.com/jobs/01684d0c-4acd-465a-8f1a-34b32ab7bf23/images/53730a260893d483b61190d74ff152dc43e4bdd1ca883bbc0375f3428800561b.png"
        alt=""
        className="w-full h-full object-contain"
      />
    </div>

    <div className="max-w-7xl mx-auto relative z-10">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="mb-16"
      >
        <p className="text-sm tracking-widest uppercase text-pink-deep font-sans mb-4">Selected Works</p>
        <h2 className="text-4xl md:text-5xl font-serif font-semibold tracking-tight text-brown-deep" data-testid="work-section-title">
          Featured Projects
        </h2>
      </motion.div>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
      >
        {works.map((work, index) => (
          <WorkCard key={work.title} work={work} index={index} />
        ))}
      </motion.div>
    </div>
  </section>
);

export default FeaturedWork;
